/** Invitation URL parsing and web-to-native hand-off. */

const INVITE_TOKEN = /^[A-Za-z0-9_-]+$/;

const LOCAL_MEMBER_ORIGINS = [
  "http://localhost:3002",
  "http://127.0.0.1:3002",
] as const;

const STORE_FALLBACK: Record<StorePlatform, string> = {
  // Apple has a stable country storefront search page even before the exact listing
  // ID is known. Production can replace it with the direct app URL through Vite env.
  ios: "https://apps.apple.com/cz/iphone/search?term=Elyxeer%2030.5",
  android: "https://play.google.com/store/search?q=Elyxeer%2030.5&c=apps",
};

export const INVITE_PATH_PREFIX = "/invite/";
export const INVITE_SCHEME = "elyxeer";

function routeForToken(token: string): string | null {
  return INVITE_TOKEN.test(token) ? `${INVITE_PATH_PREFIX}${token}` : null;
}

function routeFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/invite\/([^/]+)\/?$/);
  if (!match) return null;
  try {
    return routeForToken(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

function httpOrigin(rawUrl: string | undefined): string | null {
  if (!rawUrl?.trim()) return null;
  try {
    const url = new URL(rawUrl);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

/** Exact origins that are allowed to deliver an HTTP(S) invitation into the app. */
function memberOrigins(): Set<string> {
  const origins = new Set<string>(LOCAL_MEMBER_ORIGINS);
  const configured = httpOrigin(import.meta.env.VITE_MEMBER_APP_URL);
  if (configured) origins.add(configured);
  if (typeof window !== "undefined") {
    const current = httpOrigin(window.location.href);
    if (current) origins.add(current);
  }
  return origins;
}

/**
 * Turn a shared HTTPS link, hash-router URL, or native custom URL into an app route.
 * Only the canonical invite shape is accepted; unrelated application URLs are ignored.
 */
export function inviteRouteFromUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    // A base permits current-page hash/relative links, but the resolved HTTP origin
    // still has to pass the explicit allow-list below.
    const base =
      typeof window !== "undefined"
        ? window.location.href
        : LOCAL_MEMBER_ORIGINS[0];
    url = new URL(rawUrl, base);
  } catch {
    return null;
  }

  if (url.protocol === `${INVITE_SCHEME}:`) {
    if (url.hostname !== "invite") return null;
    return routeForToken(url.pathname.replace(/^\//, ""));
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (!memberOrigins().has(url.origin)) return null;

  // HashRouter keeps its search string inside the fragment. Routing depends only on
  // the path; campaign parameters must not become part of the bearer token.
  const hashPath = url.hash.replace(/^#/, "").split("?", 1)[0];
  return routeFromPath(hashPath) ?? routeFromPath(url.pathname);
}

/** The custom-scheme URL installed iOS and Android builds register. */
export function nativeInviteUrl(token: string): string {
  const route = routeForToken(token);
  if (!route) throw new Error("Invalid membership invitation token");
  return `${INVITE_SCHEME}://${route.slice(1)}`;
}

/**
 * Convert a directly requested public path into the hash route the web/native bundle
 * uses. This runs before React mounts so the invite page is the first painted frame.
 */
export function normalizeDirectInviteLocation(): void {
  const route = routeFromPath(window.location.pathname);
  const hashPath = window.location.hash.replace(/^#/, "").split("?", 1)[0];
  if (!route || routeFromPath(hashPath)) return;
  window.history.replaceState(
    window.history.state,
    "",
    `/#${route}${window.location.search}`,
  );
}

export type StorePlatform = "ios" | "android";

/** Platform whose store can handle this browser, when it is recognisably mobile. */
export function storePlatform(
  userAgent = navigator.userAgent,
): StorePlatform | null {
  if (/android/i.test(userAgent)) return "android";
  if (/iPad|iPhone|iPod/i.test(userAgent)) return "ios";
  // Modern iPadOS identifies as macOS but still has touch points.
  if (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1)
    return "ios";
  return null;
}

function storeOverride(
  platform: StorePlatform,
  rawUrl: string | undefined,
): string | null {
  if (!rawUrl?.trim()) return null;
  try {
    const url = new URL(rawUrl);
    const expectedHost =
      platform === "ios" ? "apps.apple.com" : "play.google.com";
    return url.protocol === "https:" && url.hostname === expectedHost
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

/** Direct configured listing, or a platform-store search that is always available. */
export function configuredStoreUrl(
  detected: StorePlatform | null = storePlatform(),
): string | null {
  if (!detected) return null;
  const value =
    detected === "ios"
      ? import.meta.env.VITE_IOS_APP_STORE_URL
      : import.meta.env.VITE_GOOGLE_PLAY_STORE_URL;
  return storeOverride(detected, value) ?? STORE_FALLBACK[detected];
}

/**
 * Ask the OS to open the installed app, then fall through to its store listing when
 * the browser stays visible. Returning cleanup lets React cancel the pending hand-off.
 */
export function openNativeInvite(
  token: string,
  fallbackUrl = configuredStoreUrl(),
): () => void {
  let timer: number | undefined;
  const cancel = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
  const handleVisibility = () => {
    if (document.visibilityState === "hidden") cancel();
  };

  document.addEventListener("visibilitychange", handleVisibility);
  if (fallbackUrl) {
    timer = window.setTimeout(() => {
      if (document.visibilityState === "visible")
        window.location.assign(fallbackUrl);
      cancel();
    }, 1500);
  }
  window.location.assign(nativeInviteUrl(token));
  return cancel;
}
